import { createContext, useContext, useEffect, useRef, useState } from "react";
import { api } from "@/lib/api";

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const operationRef = useRef(0);

  useEffect(() => {
    const operation = ++operationRef.current;
    api.get("/auth/session").then(({ data }) => {
      if (operation === operationRef.current) setUser(data.user || false);
    }).catch(() => {
      if (operation === operationRef.current) setUser(false);
    }).finally(() => {
      if (operation === operationRef.current) setLoading(false);
    });
  }, []);

  const confirmSession = async (fallback) => {
    for (let attempt = 0; attempt < 3; attempt += 1) {
      try { const { data } = await api.get("/auth/session"); if (data.user) return data.user; }
      catch { /* retry below */ }
      await new Promise((resolve) => window.setTimeout(resolve, 120 * (attempt + 1)));
    }
    return fallback;
  };

  const login = async (payload) => {
    const operation = ++operationRef.current;
    const { data } = await api.post("/auth/login", payload);
    const confirmed = await confirmSession(data);
    if (operation === operationRef.current) { setUser(confirmed); setLoading(false); }
    return confirmed;
  };

  const registerCoach = async (payload) => {
    const operation = ++operationRef.current;
    const { data } = await api.post("/auth/register-coach", payload);
    const confirmed = await confirmSession(data);
    if (operation === operationRef.current) { setUser(confirmed); setLoading(false); }
    return confirmed;
  };

  const logout = async () => {
    const operation = ++operationRef.current;
    try { await api.post("/auth/logout"); }
    finally { if (operation === operationRef.current) { setUser(false); setLoading(false); } }
  };

  const refreshUser = async () => {
    const operation = ++operationRef.current;
    const { data } = await api.get("/auth/me");
    if (operation === operationRef.current) setUser(data);
    return data;
  };

  return <AuthContext.Provider value={{ user, loading, login, registerCoach, logout, refreshUser }}>{children}</AuthContext.Provider>;
}

export const useAuth = () => useContext(AuthContext);